import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { IntendedDisplay, SmartSort } from '@iheartjane/dm-sdk';
import { hideStrikethroughPrice } from '@iheartjane/menu';
import type {
  BannerCarouselSlide,
  BannerCustomImageAdSlide,
  CustomImageAdProduct,
  ProductCardPrice,
  WebCustomImageAdEvent,
} from '@iheartjane/menu';
import type { StoreCarouselBanner } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import compact from 'lodash-es/compact';
import sortBy from 'lodash-es/sortBy';

import { findSpecialForProduct } from '@jane/shared-ecomm/components';
import { PRICE_LOOKUP } from '@jane/shared/components';
// eslint-disable-next-line no-restricted-imports
import type { StoreSpecial } from '@jane/shared/models';
import type { StoreSpecial as StoreSpecialType } from '@jane/shared/types';
import {
  defaultWeightIdForMenuProduct,
  getDiscountedUnitPrice,
  getUnitPrice,
} from '@jane/shared/util';

interface BuildCustomImageAdSlideProps {
  bundleTag?: boolean;
  instance: SmartSort<CustomImageAdProduct> | undefined;
  intendedDisplay: IntendedDisplay;
  onEvent?: (event: WebCustomImageAdEvent) => void;
  onImageClick?: (brandName: string) => void;
  showPrice?: boolean;
  specials?: StoreSpecialType[];
  state?: string | null;
  toPdpUrl: (product: CustomImageAdProduct) => string;
}

// mirrors the Boost product card (MenuProductInfo): one price for the default
// weight, discounted only by a product special
const getProductCardPrice = (
  product: CustomImageAdProduct,
  specials?: StoreSpecialType[],
  bundleTag?: boolean,
  state?: string | null
): ProductCardPrice => {
  // ad attributes are the full menu product record, typed only by what the card shows
  const menuProduct = product as MaterializedMenuProduct;
  const special = findSpecialForProduct(
    menuProduct,
    specials as StoreSpecial[],
    bundleTag
  );
  const defaultWeight = defaultWeightIdForMenuProduct({
    menuProduct,
    special,
  });
  const originalPrice = getUnitPrice(menuProduct, defaultWeight);
  const discountedPrice = getDiscountedUnitPrice(menuProduct, defaultWeight);
  const price =
    special?.special_type === 'product' && discountedPrice
      ? discountedPrice
      : originalPrice;

  if (price === undefined) return {};

  const weightLabel = menuProduct.amount
    ? undefined
    : PRICE_LOOKUP[defaultWeight.toUpperCase()];
  const isDiscounted = !!originalPrice && originalPrice !== price;

  return {
    isDiscounted,
    price: compact([formatCurrency(price), weightLabel]).join('/'),
    originalPrice:
      isDiscounted && !hideStrikethroughPrice(state ?? '')
        ? formatCurrency(originalPrice)
        : undefined,
  };
};

export const buildCustomImageAdSlide = ({
  bundleTag,
  instance,
  intendedDisplay,
  onEvent,
  onImageClick,
  showPrice,
  specials,
  state,
  toPdpUrl,
}: BuildCustomImageAdSlideProps): BannerCustomImageAdSlide | undefined => {
  if (!instance?.products.length) return undefined;

  const customImageData = instance.customImages.find(
    (image) => image.intendedDisplay === intendedDisplay
  );

  if (!customImageData) return undefined;

  return {
    customImageData,
    id: 'carousel-image-ad',
    kind: 'customImageAd',
    onEvent,
    onImageClick,
    // the Boost product card shows every price in the primary color
    priceColors: {
      deal: 'primary',
      regular: 'primary',
      strikethrough: 'primary',
    },
    products: instance.products.map((product) => ({
      ...product.attributes,
      ...getProductCardPrice(product.attributes, specials, bundleTag, state),
      clickHandler: () => void product.click(),
      pdpUrl: toPdpUrl(product.attributes),
    })),
    showPrice,
  };
};

export const getMobileCarouselSlides = (
  mobileCarouselSlides: StoreCarouselBanner[],
  desktopCarouselSlides: StoreCarouselBanner[]
) => {
  if (!mobileCarouselSlides) {
    return desktopCarouselSlides;
  }

  const missingSlides = desktopCarouselSlides.filter(
    (slideA) =>
      !mobileCarouselSlides.some(
        (slideB) => slideB.position === slideA.position
      )
  );

  return sortBy([...missingSlides, ...mobileCarouselSlides], 'position');
};

export const toBannerCarouselSlides = (
  slides: StoreCarouselBanner[],
  storeName?: string
): BannerCarouselSlide[] =>
  slides.map((slide) => ({
    id: String(slide.id),
    kind: 'image',
    imageAlt: slide.alt_text ?? `${storeName} Marketing Banner`,
    imageSrc: slide.image_url,
    url: slide.url,
  }));

export const storeHasVisibleCarousel = (store: {
  carousel_banner?: readonly StoreCarouselBanner[] | null;
}) =>
  store?.carousel_banner &&
  store?.carousel_banner.length > 0 &&
  store?.carousel_banner.every((slide) => slide.visible);
